import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import extractApiError from '../../utils/apiError';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';
import { parseTutarTR, formatTutarTR } from '../../utils/para';
import SearchableSelect from '../../components/SearchableSelect';

// PR-D (PD4) — Dış Müşteri Satışı: İade yönetimi (iade irsaliyesi + iade faturası).
// İki sekme tek sayfada:
//   1) İade İrsaliyeleri — /api/satis-iade-irsaliyeleri (liste + onayla/iptal + yeni form)
//   2) İade Faturaları   — /api/satis-iade-faturalari   (liste + iptal + yeni form)
// Yeni iade irsaliyesi: kaynak irsaliye seç → kalem bazında iade miktarı gir.
// Yeni iade faturası: müşteri + kaynak fatura + ONAYLANDI iade irsaliyeleri seç → kes.
// Anayasa M.5: MANAGE_SATIS ile mutasyon butonları gizlenir (backend zaten enforce eder).

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Durum → rozet rengi. İade irsaliyesi (TASLAK/ONAYLANDI/FATURALANDI/IPTAL) +
// iade faturası (TASLAK/KESILDI/IPTAL) için tek harita yeterli.
const DURUM_BADGE = {
  TASLAK: { bg: '#e2e3e5', fg: '#383d41', label: 'Taslak' },
  ONAYLANDI: { bg: '#cce5ff', fg: '#004085', label: 'Onaylandı' },
  FATURALANDI: { bg: '#d4edda', fg: '#155724', label: 'Faturalandı' },
  KESILDI: { bg: '#d4edda', fg: '#155724', label: 'Kesildi' },
  IPTAL: { bg: '#f8d7da', fg: '#721c24', label: 'İptal' },
};

const fmtTarih = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString('tr-TR');
};

const todayStr = () => new Date().toISOString().split('T')[0];
const addDaysStr = (gun) => {
  const d = new Date();
  d.setDate(d.getDate() + (gun || 0));
  return d.toISOString().split('T')[0];
};

const DurumBadge = ({ durum }) => {
  const b = DURUM_BADGE[durum] || { bg: colors.backgroundAlt, fg: colors.textSecondary, label: durum || '-' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
      background: b.bg, color: b.fg, whiteSpace: 'nowrap',
    }}>
      {b.label}
    </span>
  );
};

const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#555' };
const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box',
};

// ============================================================================
// Yeni İade İrsaliyesi formu (modal)
// Kaynak irsaliye seç (SEVK_EDILDI + FATURALANDI) → kalem bazında iade miktarı.
// ============================================================================
const YeniIadeIrsaliyesiModal = ({ token, cariMap, onClose, onSaved }) => {
  const toast = useToast();
  const [kaynakIrsaliyeler, setKaynakIrsaliyeler] = useState([]);
  const [malzemeMap, setMalzemeMap] = useState({}); // malzeme_id → ad
  const [kaynakId, setKaynakId] = useState('');
  const [detay, setDetay] = useState(null); // seçilen irsaliyenin detayı (kalemler)
  const [detayLoading, setDetayLoading] = useState(false);
  const [miktarlar, setMiktarlar] = useState({}); // kaynak_irsaliye_kalem_id → iade miktarı (string)
  const [tarih, setTarih] = useState(todayStr());
  const [notlar, setNotlar] = useState('');
  const [saving, setSaving] = useState(false);

  // Kaynak irsaliyeler: SEVK_EDILDI + FATURALANDI (iade edilebilir = sevk veya faturalı).
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/satis-irsaliyeleri?durum=SEVK_EDILDI`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API}/api/satis-irsaliyeleri?durum=FATURALANDI`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([sevk, fat]) => {
      const list = [...(Array.isArray(sevk) ? sevk : []), ...(Array.isArray(fat) ? fat : [])];
      setKaynakIrsaliyeler(list);
    });
    fetch(`${API}/api/malzemeler`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const map = {};
        (Array.isArray(data) ? data : []).forEach((m) => { map[m.id] = m.ad; });
        setMalzemeMap(map);
      })
      .catch(() => {});
  }, [token]);

  const handleKaynakChange = useCallback(async (val) => {
    setKaynakId(val);
    setDetay(null);
    setMiktarlar({});
    if (!val) return;
    setDetayLoading(true);
    try {
      const res = await fetch(`${API}/api/satis-irsaliyeleri/${val}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDetay(data);
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('Kaynak irsaliye detay hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setDetayLoading(false);
    }
  }, [token, toast]);

  const malzemeAd = (id) => malzemeMap[id] || `#${id}`;

  const kalemler = (detay && Array.isArray(detay.kalemler)) ? detay.kalemler : [];
  const gecerliKalemler = kalemler
    .map((k) => ({ kalem: k, miktar: parseTutarTR(miktarlar[k.id]) || 0 }))
    .filter((x) => x.miktar > 0);
  const fazlaMiktar = kalemler.some((k) => (parseTutarTR(miktarlar[k.id]) || 0) > (Number(k.miktar) || 0));
  const kaydetDisabled = saving || gecerliKalemler.length === 0 || fazlaMiktar;

  const handleKaydet = async () => {
    if (!kaynakId) { toast.error('Kaynak irsaliye seçiniz'); return; }
    if (gecerliKalemler.length === 0) { toast.error('En az bir kalem için iade miktarı giriniz'); return; }
    if (fazlaMiktar) { toast.error('İade miktarı orijinal miktarı aşamaz'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/satis-iade-irsaliyeleri`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          kaynak_irsaliye_id: parseInt(kaynakId, 10),
          tarih,
          notlar: notlar || null,
          kalemler: gecerliKalemler.map((x) => ({
            kaynak_irsaliye_kalem_id: x.kalem.id,
            miktar: x.miktar,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`İade irsaliyesi oluşturuldu: ${data.iade_irsaliye_no || ''}`);
        onSaved();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İade irsaliyesi kaydetme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const kaynakOptions = kaynakIrsaliyeler.map((ir) => ({
    value: String(ir.id),
    label: `${ir.irsaliye_no || `#${ir.id}`} — ${cariMap[ir.cari_id] || `#${ir.cari_id}`}`,
  }));

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
    }}>
      <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.lg, width: '720px', maxWidth: '95vw', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Yeni İade İrsaliyesi</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Kaynak İrsaliye *</label>
            <SearchableSelect
              options={kaynakOptions}
              value={kaynakId}
              onChange={handleKaynakChange}
              placeholder="İrsaliye ara…"
            />
          </div>
          <div>
            <label style={labelStyle}>İade Tarihi *</label>
            <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Kalemler */}
        {kaynakId && detayLoading && (
          <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontSize: '14px' }}>
            Kalemler yükleniyor…
          </div>
        )}

        {kaynakId && !detayLoading && detay && (
          <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Malzeme</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', width: '120px' }}>Orijinal Miktar</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', width: '80px' }}>Birim</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', width: '140px' }}>İade Miktarı</th>
                </tr>
              </thead>
              <tbody>
                {kalemler.map((k) => {
                  const girilen = parseTutarTR(miktarlar[k.id]) || 0;
                  const asti = girilen > (Number(k.miktar) || 0);
                  return (
                    <tr key={k.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: '8px 10px' }}>{malzemeAd(k.malzeme_id)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.miktar)}</td>
                      <td style={{ padding: '8px 10px' }}>{k.birim || '-'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <input
                          type="text"
                          value={miktarlar[k.id] || ''}
                          onChange={(e) => setMiktarlar((prev) => ({ ...prev, [k.id]: e.target.value }))}
                          placeholder="0"
                          aria-label={`${malzemeAd(k.malzeme_id)} iade miktarı`}
                          style={{ ...inputStyle, textAlign: 'right', borderColor: asti ? '#d32f2f' : colors.border }}
                        />
                      </td>
                    </tr>
                  );
                })}
                {kalemler.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '14px', color: colors.textTertiary }}>Bu irsaliyede kalem yok.</td></tr>
                )}
              </tbody>
            </table>
            {fazlaMiktar && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#d32f2f' }}>
                İade miktarı orijinal miktarı aşamaz.
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Notlar</label>
          <input
            type="text"
            value={notlar}
            onChange={(e) => setNotlar(e.target.value)}
            placeholder="İade notu (opsiyonel)…"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '12px 24px', background: colors.background, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
          >
            İptal
          </button>
          <button
            onClick={handleKaydet}
            disabled={kaydetDisabled}
            title={gecerliKalemler.length === 0 ? 'En az bir kalem için iade miktarı giriniz' : undefined}
            style={{
              padding: '12px 32px', border: 'none', borderRadius: radius.sm,
              background: kaydetDisabled ? '#90caf9' : styles.primary, color: 'white',
              cursor: kaydetDisabled ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px',
            }}
          >
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Yeni İade Faturası formu (modal)
// Müşteri → kaynak fatura (KESILDI) + ONAYLANDI iade irsaliyeleri (faturasız) → kes.
// ============================================================================
const YeniIadeFaturasiModal = ({ token, cariler, onClose, onSaved }) => {
  const toast = useToast();
  const [cariId, setCariId] = useState('');
  const [kaynakFaturalar, setKaynakFaturalar] = useState([]);
  const [kaynakFaturaId, setKaynakFaturaId] = useState('');
  const [iadeIrsaliyeler, setIadeIrsaliyeler] = useState([]); // ONAYLANDI + faturasız
  const [secili, setSecili] = useState({}); // iade_irsaliye_id → true
  const [tarih, setTarih] = useState(todayStr());
  const [vadeTarihi, setVadeTarihi] = useState(addDaysStr(30));
  const [notlar, setNotlar] = useState('');
  const [loadingKaynak, setLoadingKaynak] = useState(false);
  const [saving, setSaving] = useState(false);

  // Müşteri seçilince: kaynak faturaları (KESILDI) + ONAYLANDI iade irsaliyelerini çek.
  const fetchKaynaklar = useCallback(async (selectedCariId) => {
    setKaynakFaturalar([]);
    setKaynakFaturaId('');
    setIadeIrsaliyeler([]);
    setSecili({});
    if (!selectedCariId) return;
    setLoadingKaynak(true);
    try {
      const [fatRes, iadeRes] = await Promise.all([
        fetch(`${API}/api/satis-faturalari?durum=KESILDI&cari_id=${selectedCariId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/satis-iade-irsaliyeleri?durum=ONAYLANDI&cari_id=${selectedCariId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const fatData = await fatRes.json().catch(() => []);
      const iadeData = await iadeRes.json().catch(() => []);
      if (!fatRes.ok) { toast.error(extractApiError(fatData.detail)); return; }
      if (!iadeRes.ok) { toast.error(extractApiError(iadeData.detail)); return; }
      setKaynakFaturalar(Array.isArray(fatData) ? fatData : []);
      // ONAYLANDI + henüz iade-faturalanmamış (satis_iade_fatura_id null) — istemci tarafı güvenlik süzgeci.
      setIadeIrsaliyeler((Array.isArray(iadeData) ? iadeData : []).filter((ir) => !ir.satis_iade_fatura_id));
    } catch (err) {
      logError('İade faturası kaynak yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoadingKaynak(false);
    }
  }, [token, toast]);

  const handleCariChange = (val) => {
    setCariId(val);
    const c = cariler.find((x) => String(x.id) === String(val));
    if (c && c.vade_gun != null) setVadeTarihi(addDaysStr(c.vade_gun));
    fetchKaynaklar(val);
  };

  const toggleSecim = (id) => setSecili((prev) => ({ ...prev, [id]: !prev[id] }));
  const seciliIdler = iadeIrsaliyeler.filter((ir) => secili[ir.id]).map((ir) => ir.id);
  const kaydetDisabled = saving || !kaynakFaturaId || seciliIdler.length === 0;

  const handleKaydet = async () => {
    if (!cariId) { toast.error('Müşteri seçiniz'); return; }
    if (!kaynakFaturaId) { toast.error('Kaynak fatura seçiniz'); return; }
    if (!tarih) { toast.error('Tarih giriniz'); return; }
    if (!vadeTarihi) { toast.error('Vade tarihi giriniz'); return; }
    if (seciliIdler.length === 0) { toast.error('En az bir iade irsaliyesi seçiniz'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/satis-iade-faturalari`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          kaynak_fatura_id: parseInt(kaynakFaturaId, 10),
          tarih,
          vade_tarihi: vadeTarihi,
          notlar: notlar || null,
          iade_irsaliye_ids: seciliIdler,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`İade faturası kesildi: ${data.iade_fatura_no || ''}`);
        onSaved();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İade faturası kesme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
    }}>
      <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.lg, width: '680px', maxWidth: '95vw', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Yeni İade Faturası</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Müşteri *</label>
            <SearchableSelect
              options={cariler.map((c) => ({ value: String(c.id), label: c.unvan }))}
              value={cariId}
              onChange={handleCariChange}
              placeholder="Müşteri ara…"
            />
          </div>
          <div>
            <label style={labelStyle}>Kaynak Fatura *</label>
            <select value={kaynakFaturaId} onChange={(e) => setKaynakFaturaId(e.target.value)} style={inputStyle} disabled={!cariId}>
              <option value="">Fatura Seçiniz</option>
              {kaynakFaturalar.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {f.fatura_no || `#${f.id}`}{f.toplam_brut != null ? ` — ${formatTutarTR(f.toplam_brut)} TL` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Tarih *</label>
            <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Vade Tarihi *</label>
            <input type="date" value={vadeTarihi} onChange={(e) => setVadeTarihi(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* ONAYLANDI iade irsaliyeleri checklist */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>İade İrsaliyeleri (onaylanmış, faturasız) *</label>
          {!cariId && (
            <div style={{ padding: '16px', textAlign: 'center', color: colors.textTertiary, fontSize: '14px' }}>
              Önce müşteri seçiniz.
            </div>
          )}
          {cariId && loadingKaynak && (
            <div style={{ padding: '16px', textAlign: 'center', color: colors.textSecondary, fontSize: '14px' }}>
              Yükleniyor…
            </div>
          )}
          {cariId && !loadingKaynak && iadeIrsaliyeler.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: colors.textTertiary, fontSize: '14px' }}>
              Bu müşterinin onaylanmış, henüz faturalanmamış iade irsaliyesi yok.
            </div>
          )}
          {cariId && !loadingKaynak && iadeIrsaliyeler.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: '8px 10px', width: '40px', textAlign: 'center' }}>Seç</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>İade No</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Tarih</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Kaynak İrsaliye</th>
                  </tr>
                </thead>
                <tbody>
                  {iadeIrsaliyeler.map((ir) => (
                    <tr
                      key={ir.id}
                      onClick={() => toggleSecim(ir.id)}
                      style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', background: secili[ir.id] ? colors.infoLight : 'white' }}
                    >
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!secili[ir.id]}
                          onChange={() => toggleSecim(ir.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`${ir.iade_irsaliye_no || ir.id} seç`}
                        />
                      </td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{ir.iade_irsaliye_no || `#${ir.id}`}</td>
                      <td style={{ padding: '8px 10px' }}>{fmtTarih(ir.tarih)}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '13px' }}>#{ir.kaynak_irsaliye_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '8px', textAlign: 'right', fontSize: '13px', color: colors.textSecondary }}>
                Seçilen iade irsaliyesi: <strong>{seciliIdler.length}</strong>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Notlar</label>
          <input
            type="text"
            value={notlar}
            onChange={(e) => setNotlar(e.target.value)}
            placeholder="İade faturası notu (opsiyonel)…"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '12px 24px', background: colors.background, color: colors.textPrimary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
          >
            İptal
          </button>
          <button
            onClick={handleKaydet}
            disabled={kaydetDisabled}
            title={seciliIdler.length === 0 ? 'En az bir iade irsaliyesi seçiniz' : undefined}
            style={{
              padding: '12px 32px', border: 'none', borderRadius: radius.sm,
              background: kaydetDisabled ? '#90caf9' : styles.primary, color: 'white',
              cursor: kaydetDisabled ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px',
            }}
          >
            {saving ? 'Kesiliyor…' : 'İade Faturası Kes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Tab 1: İade İrsaliyeleri listesi
// ============================================================================
const IRSALIYE_DURUM_FILTRE = [
  { value: '', label: 'Tümü' },
  { value: 'TASLAK', label: 'Taslak' },
  { value: 'ONAYLANDI', label: 'Onaylandı' },
  { value: 'FATURALANDI', label: 'Faturalandı' },
  { value: 'IPTAL', label: 'İptal' },
];

const IadeIrsaliyeleriTab = ({ token, canManage, cariMap }) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detayId, setDetayId] = useState(null);
  const [detay, setDetay] = useState(null);
  const [detayLoading, setDetayLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [durum, setDurum] = useUrlState('iade_durum', '');
  const [arama, setArama] = useUrlState('iade_q', '', { debounceMs: 300 });

  const fetchRows = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (durum) params.set('durum', durum);
      const res = await fetch(`${API}/api/satis-iade-irsaliyeleri?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { toast.error(extractApiError(data.detail)); return; }
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('İade irsaliyesi yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, durum, toast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const cariAd = (id) => cariMap[id] || `#${id}`;

  const handleDetay = async (iade) => {
    if (detayId === iade.id) { setDetayId(null); setDetay(null); return; }
    setDetayId(iade.id);
    setDetay(null);
    setDetayLoading(true);
    try {
      const res = await fetch(`${API}/api/satis-iade-irsaliyeleri/${iade.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDetay(data);
      } else {
        toast.error(extractApiError(data.detail));
        setDetayId(null);
      }
    } catch (err) {
      logError('İade irsaliyesi detay hatası:', err);
      toast.error('Bağlantı hatası');
      setDetayId(null);
    } finally {
      setDetayLoading(false);
    }
  };

  const handleOnayla = async (iade) => {
    if (!window.confirm(
      `"${iade.iade_irsaliye_no}" iade irsaliyesini onaylamak istediğinize emin misiniz?\n\nİade edilen mal stoğa geri girer.`,
    )) return;
    setBusyId(iade.id);
    try {
      const res = await fetch(`${API}/api/satis-iade-irsaliyeleri/${iade.id}/onayla`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${iade.iade_irsaliye_no}" onaylandı`);
        fetchRows();
      } else {
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İade irsaliyesi onaylama hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  const handleIptal = async (iade) => {
    if (!window.confirm(
      `"${iade.iade_irsaliye_no}" iade irsaliyesini iptal etmek istediğinize emin misiniz?\n\nOnaylanmışsa stok geri çıkarılır (storno).`,
    )) return;
    setBusyId(iade.id);
    try {
      const res = await fetch(`${API}/api/satis-iade-irsaliyeleri/${iade.id}/iptal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${iade.iade_irsaliye_no}" iptal edildi`);
        if (detayId === iade.id) { setDetayId(null); setDetay(null); }
        fetchRows();
      } else {
        // 409: faturalanmış iade irsaliyesi iptal edilemez
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İade irsaliyesi iptal hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  const gorunenRows = rows.filter((r) => {
    if (!arama) return true;
    const q = arama.toLocaleLowerCase('tr-TR');
    const no = (r.iade_irsaliye_no || '').toLocaleLowerCase('tr-TR');
    const ad = (cariMap[r.cari_id] || '').toLocaleLowerCase('tr-TR');
    return no.includes(q) || ad.includes(q);
  });

  if (loading && rows.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>;
  }

  return (
    <div>
      {/* Filtreler + yeni butonu */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          value={durum}
          onChange={(e) => setDurum(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px', fontWeight: 'bold' }}
        >
          {IRSALIYE_DURUM_FILTRE.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
        <input
          type="text"
          placeholder="İade no veya müşteri ara…"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          style={{ flex: 1, minWidth: '220px', padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }}
        />
        {canManage && (
          <button
            onClick={() => setShowForm(true)}
            style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
          >
            + Yeni İade İrsaliyesi
          </button>
        )}
      </div>

      {/* Tablo */}
      <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>İade No</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tarih</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Müşteri</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Kaynak İrsaliye</th>
                <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', width: '260px' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {gorunenRows.map((iade) => (
                <React.Fragment key={iade.id}>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{iade.iade_irsaliye_no || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtTarih(iade.tarih)}</td>
                    <td style={{ padding: '10px 12px' }}><strong>{cariAd(iade.cari_id)}</strong></td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px' }}>#{iade.kaynak_irsaliye_id}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><DurumBadge durum={iade.durum} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleDetay(iade)}
                          style={{ padding: '6px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: detayId === iade.id ? colors.backgroundAlt : 'white', color: colors.textPrimary, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                        >
                          {detayId === iade.id ? 'Gizle' : 'Detay'}
                        </button>
                        {canManage && iade.durum === 'TASLAK' && (
                          <button
                            onClick={() => handleOnayla(iade)}
                            disabled={busyId === iade.id}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#198754', color: 'white', cursor: busyId === iade.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            Onayla
                          </button>
                        )}
                        {canManage && (iade.durum === 'TASLAK' || iade.durum === 'ONAYLANDI') && (
                          <button
                            onClick={() => handleIptal(iade)}
                            disabled={busyId === iade.id}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#d32f2f', color: 'white', cursor: busyId === iade.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            İptal
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {detayId === iade.id && (
                    <tr style={{ borderBottom: `1px solid ${colors.border}`, background: colors.backgroundAlt }}>
                      <td colSpan={6} style={{ padding: '12px 16px' }}>
                        {detayLoading && <div style={{ color: colors.textSecondary, fontSize: '13px' }}>Detay yükleniyor…</div>}
                        {!detayLoading && detay && (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Malzeme</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>İade Miktarı</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Birim</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Birim Fiyat</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Brüt</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(detay.kalemler || []).map((k) => (
                                <tr key={k.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '6px 8px' }}>#{k.malzeme_id}</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.miktar)}</td>
                                  <td style={{ padding: '6px 8px' }}>{k.birim || '-'}</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.birim_fiyat)}</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatTutarTR(k.satir_brut)}</td>
                                </tr>
                              ))}
                              {(detay.kalemler || []).length === 0 && (
                                <tr><td colSpan={5} style={{ padding: '10px', color: colors.textTertiary }}>Kalem yok.</td></tr>
                              )}
                            </tbody>
                          </table>
                        )}
                        {!detayLoading && detay && detay.notlar && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: colors.textSecondary }}>Not: {detay.notlar}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {gorunenRows.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                    {arama || durum ? 'Kritere uygun iade irsaliyesi bulunamadı.' : 'Henüz iade irsaliyesi oluşturulmamış.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <YeniIadeIrsaliyesiModal
          token={token}
          cariMap={cariMap}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchRows(); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// Tab 2: İade Faturaları listesi
// ============================================================================
const FATURA_DURUM_FILTRE = [
  { value: '', label: 'Tümü' },
  { value: 'TASLAK', label: 'Taslak' },
  { value: 'KESILDI', label: 'Kesildi' },
  { value: 'IPTAL', label: 'İptal' },
];

const IadeFaturalariTab = ({ token, canManage, cariMap, cariler }) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detayId, setDetayId] = useState(null);
  const [detay, setDetay] = useState(null);
  const [detayLoading, setDetayLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [durum, setDurum] = useUrlState('iadefat_durum', '');
  const [arama, setArama] = useUrlState('iadefat_q', '', { debounceMs: 300 });

  const fetchRows = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (durum) params.set('durum', durum);
      const res = await fetch(`${API}/api/satis-iade-faturalari?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { toast.error(extractApiError(data.detail)); return; }
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('İade faturası yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [token, durum, toast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const cariAd = (id) => cariMap[id] || `#${id}`;

  const handleDetay = async (f) => {
    if (detayId === f.id) { setDetayId(null); setDetay(null); return; }
    setDetayId(f.id);
    setDetay(null);
    setDetayLoading(true);
    try {
      const res = await fetch(`${API}/api/satis-iade-faturalari/${f.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDetay(data);
      } else {
        toast.error(extractApiError(data.detail));
        setDetayId(null);
      }
    } catch (err) {
      logError('İade faturası detay hatası:', err);
      toast.error('Bağlantı hatası');
      setDetayId(null);
    } finally {
      setDetayLoading(false);
    }
  };

  const handleIptal = async (f) => {
    if (!window.confirm(
      `"${f.iade_fatura_no}" iade faturasını iptal etmek istediğinize emin misiniz?\n\nİade irsaliyeleri tekrar faturalanabilir duruma döner.`,
    )) return;
    setBusyId(f.id);
    try {
      const res = await fetch(`${API}/api/satis-iade-faturalari/${f.id}/iptal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`"${f.iade_fatura_no}" iptal edildi`);
        if (detayId === f.id) { setDetayId(null); setDetay(null); }
        fetchRows();
      } else {
        // 409: tahsilatı/mahsubu başlamış iade faturası iptal edilemez
        toast.error(extractApiError(data.detail));
      }
    } catch (err) {
      logError('İade faturası iptal hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setBusyId(null);
    }
  };

  const gorunenRows = rows.filter((r) => {
    if (!arama) return true;
    const q = arama.toLocaleLowerCase('tr-TR');
    const no = (r.iade_fatura_no || '').toLocaleLowerCase('tr-TR');
    const ad = (cariMap[r.cari_id] || '').toLocaleLowerCase('tr-TR');
    return no.includes(q) || ad.includes(q);
  });

  if (loading && rows.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>;
  }

  return (
    <div>
      {/* Filtreler + yeni butonu */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          value={durum}
          onChange={(e) => setDurum(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px', fontWeight: 'bold' }}
        >
          {FATURA_DURUM_FILTRE.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
        <input
          type="text"
          placeholder="İade fatura no veya müşteri ara…"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          style={{ flex: 1, minWidth: '220px', padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }}
        />
        {canManage && (
          <button
            onClick={() => setShowForm(true)}
            style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
          >
            + Yeni İade Faturası
          </button>
        )}
      </div>

      {/* Tablo */}
      <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>İade Fatura No</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tarih</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Müşteri</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Kaynak Fatura</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Tutar</th>
                <th style={{ padding: '10px 12px', textAlign: 'center' }}>Durum</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', width: '180px' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {gorunenRows.map((f) => (
                <React.Fragment key={f.id}>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px', color: '#1565c0' }}>{f.iade_fatura_no || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtTarih(f.tarih)}</td>
                    <td style={{ padding: '10px 12px' }}><strong>{cariAd(f.cari_id)}</strong></td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px' }}>#{f.kaynak_fatura_id}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {f.toplam_brut != null ? `${formatTutarTR(f.toplam_brut)} TL` : '-'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><DurumBadge durum={f.durum} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleDetay(f)}
                          style={{ padding: '6px 12px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: detayId === f.id ? colors.backgroundAlt : 'white', color: colors.textPrimary, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                        >
                          {detayId === f.id ? 'Gizle' : 'Detay'}
                        </button>
                        {canManage && f.durum === 'KESILDI' && (
                          <button
                            onClick={() => handleIptal(f)}
                            disabled={busyId === f.id}
                            style={{ padding: '6px 12px', borderRadius: radius.sm, border: 'none', background: '#d32f2f', color: 'white', cursor: busyId === f.id ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                          >
                            İptal
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {detayId === f.id && (
                    <tr style={{ borderBottom: `1px solid ${colors.border}`, background: colors.backgroundAlt }}>
                      <td colSpan={7} style={{ padding: '12px 16px' }}>
                        {detayLoading && <div style={{ color: colors.textSecondary, fontSize: '13px' }}>Detay yükleniyor…</div>}
                        {!detayLoading && detay && (
                          <div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Malzeme</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Miktar</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Birim</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Birim Fiyat</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>KDV %</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Net</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Brüt</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(detay.kalemler || []).map((k) => (
                                  <tr key={k.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '6px 8px' }}>{k.malzeme_ad_snapshot || `#${k.malzeme_id}`}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.miktar)}</td>
                                    <td style={{ padding: '6px 8px' }}>{k.birim || '-'}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.birim_fiyat)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{k.kdv_orani}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatTutarTR(k.satir_net)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{formatTutarTR(k.satir_brut)}</td>
                                  </tr>
                                ))}
                                {(detay.kalemler || []).length === 0 && (
                                  <tr><td colSpan={7} style={{ padding: '10px', color: colors.textTertiary }}>Kalem yok.</td></tr>
                                )}
                              </tbody>
                            </table>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '8px', fontSize: '13px' }}>
                              <span style={{ color: colors.textSecondary }}>Net: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(detay.toplam_net)} TL</strong></span>
                              <span style={{ color: colors.textSecondary }}>KDV: <strong style={{ fontFamily: 'monospace' }}>{formatTutarTR(detay.toplam_kdv)} TL</strong></span>
                              <span style={{ color: colors.textPrimary }}>Genel: <strong style={{ fontFamily: 'monospace', color: styles.primary }}>{formatTutarTR(detay.toplam_brut)} TL</strong></span>
                            </div>
                            {detay.notlar && (
                              <div style={{ marginTop: '6px', fontSize: '12px', color: colors.textSecondary }}>Not: {detay.notlar}</div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {gorunenRows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                    {arama || durum ? 'Kritere uygun iade faturası bulunamadı.' : 'Henüz iade faturası kesilmemiş.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <YeniIadeFaturasiModal
          token={token}
          cariler={cariler}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchRows(); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// IadelerPage — sekme kabuğu
// ============================================================================
const IadelerPage = () => {
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SATIS, user.effectivePermissions);

  const [cariMap, setCariMap] = useState({}); // cari_id → unvan
  const [cariler, setCariler] = useState([]); // SearchableSelect için tam liste
  const [tab, setTab] = useUrlState('iade_tab', 'irsaliyeler');

  const token = localStorage.getItem('access_token');

  // Müşteri eşlemesi bir kez yüklenir (her iki sekme + form paylaşır).
  useEffect(() => {
    fetch(`${API}/api/cari?tip=MUSTERI&include_inactive=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const map = {};
        list.forEach((c) => { map[c.id] = c.unvan; });
        setCariMap(map);
        setCariler(list);
      })
      .catch(() => {});
  }, [token]);

  const tabBtnStyle = (active) => ({
    padding: '10px 20px', border: 'none', borderBottom: active ? `3px solid ${styles.primary}` : '3px solid transparent',
    background: 'transparent', color: active ? styles.primary : colors.textSecondary,
    cursor: 'pointer', fontWeight: 'bold', fontSize: '15px',
  });

  return (
    <div>
      <Header title="İadeler" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <h2 style={{ margin: '0 0 16px', fontSize: '20px', color: colors.textPrimary }}>↩️ İadeler</h2>

        {/* Sekmeler */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: `1px solid ${colors.border}`, marginBottom: '20px' }}>
          <button onClick={() => setTab('irsaliyeler')} style={tabBtnStyle(tab !== 'faturalar')}>
            İade İrsaliyeleri
          </button>
          <button onClick={() => setTab('faturalar')} style={tabBtnStyle(tab === 'faturalar')}>
            İade Faturaları
          </button>
        </div>

        {tab === 'faturalar' ? (
          <IadeFaturalariTab token={token} canManage={canManage} cariMap={cariMap} cariler={cariler} />
        ) : (
          <IadeIrsaliyeleriTab token={token} canManage={canManage} cariMap={cariMap} />
        )}

      </div>
    </div>
  );
};

export default IadelerPage;
